import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils";
import { EyedropperIcon } from "@phosphor-icons/react";
import { type ChangeEvent, type FC, useRef, useState } from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";

interface ColorOption {
  value: string;
  name?: string;
  isCustom?: boolean;
}

interface ColorButtonProps extends Omit<AriaButtonProps, "onPress"> {
  color: ColorOption;
  isSelected: boolean;
  onSelect: (color: string) => void;
}

export const ColorButton: FC<ColorButtonProps> = ({
  color,
  isSelected = true,
  onSelect,
  ...props
}) => {
  return (
    <AriaButton
      {...props}
      onPress={() => onSelect(color.value)}
      className={cn(
        // base styles
        "w-3 h-3 rounded-full transition-all duration-200 bg-red-300",
        // focus
        "data-focus:outline-hidden",
        "data-focus:ring-0",
        "data-focus:ring-offset-0",
        "data-focus-visible:outline-hidden",
        "data-focus-visible:ring-1",
        "data-[focus-visible]:ring-ring",
        "data-focus-visible:ring-offset-2",
        "data-focus-visible:ring-blue-750",
        // hover
        "hover:scale-125 active:scale-95",
        // ring
        isSelected && "ring-1 ring-offset-2",
        props.className,
      )}
      style={{
        backgroundColor: color.value,
        ...(isSelected && {
          "--tw-ring-color": "#8F31B0",
        }),
        ...props.style,
      }}
    />
  );
};

interface ColorSelectorProps {
  onSelect: (color: string) => void;
  selectedColor?: string;
  className?: string;
}

const ColorSelector: FC<ColorSelectorProps> = ({
  onSelect,
  selectedColor: initialSelectedColor,
  className,
}) => {
  const defaultColorOptions: ColorOption[] = [
    {
      value: "#8E56D5",
      name: "Lavender Dream",
    },
    {
      value: "#8F31B0",
      name: "Royal Plum",
    },
    {
      value: "#C94AF6",
      name: "Cotton Candy",
    },
    {
      value: "#3600B1",
      name: "Midnight Blue",
    },
    {
      value: "#307FFF",
      name: "Ocean Breeze",
    },
    {
      value: "#0EA5E9",
      name: "Sky Blue",
    },
    {
      value: "#EF4444",
      name: "Cherry Blossom",
    },
    {
      value: "#FACC15",
      name: "Golden Sun",
    },
    {
      value: "#F97316",
      name: "Sunset Orange",
    },
    {
      value: "#C9C9C9",
      name: "Silver Mist",
    },
    {
      value: "#10B981",
      name: "Emerald Green",
    },
    {
      value: "#4ADE80",
      name: "Mint Fresh",
    },
    {
      value: "#85C24A",
      name: "Lime Zest",
    },
  ];

  // Initialize states based on the provided selectedColor
  const initializeStates = () => {
    const fallbackColor = (defaultColorOptions[0] as ColorOption).value;
    const targetColor = initialSelectedColor || fallbackColor;

    // Check if the target color exists in default options
    const isInDefaults = defaultColorOptions.some(
      (option) => option.value.toLowerCase() === targetColor.toLowerCase(),
    );

    if (isInDefaults || !initialSelectedColor) {
      return {
        selectedColor: targetColor,
        customColors: [] as ColorOption[],
      };
    }

    // If not in defaults, add to custom colors
    const customColor: ColorOption = {
      value: targetColor,
      name: `Custom ${targetColor}`,
      isCustom: true,
    };

    return {
      selectedColor: targetColor,
      customColors: [customColor],
    };
  };

  const { selectedColor: initialSelected, customColors: initialCustomColors } =
    initializeStates();

  const [selectedColor, setSelectedColor] = useState<string>(initialSelected);
  const [customColors, setCustomColors] =
    useState<ColorOption[]>(initialCustomColors);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    onSelect?.(color);
  };

  const handleCustomColorClick = () => {
    // Trigger the hidden color input
    if (colorInputRef.current) {
      colorInputRef.current.click();
    }
  };

  const handleCustomColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newColor = event.target.value;

    // Check if this color already exists in default colors or custom colors
    const allColors = [...defaultColorOptions, ...customColors];
    const colorExists = allColors.some(
      (color) => color.value.toLowerCase() === newColor.toLowerCase(),
    );

    if (!colorExists) {
      // Add the new custom color to the list
      const newCustomColor: ColorOption = {
        value: newColor,
        name: `Custom ${newColor}`,
        isCustom: true,
      };
      setCustomColors((prev) => [...prev, newCustomColor]);
    }

    // Select the new color and notify parent
    setSelectedColor(newColor);
    onSelect?.(newColor);
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className={"flex items-center gap-2 flex-wrap"}>
        {defaultColorOptions.map((option) => {
          return (
            <ColorButton
              key={option.value}
              color={option}
              isSelected={selectedColor === option.value}
              onSelect={handleColorSelect}
            />
          );
        })}

        {/* Render custom colors after default options */}
        {customColors.map((option) => {
          return (
            <ColorButton
              key={option.value}
              color={option}
              isSelected={selectedColor === option.value}
              onSelect={handleColorSelect}
            />
          );
        })}
      </div>
      <Button
        variant={"ghost"}
        className={"flex items-center gap-1.5"}
        size={"sm"}
        onClick={handleCustomColorClick}
      >
        <EyedropperIcon size={16} />
        Custom
      </Button>

      {/* Hidden color input for OS color picker */}
      <input
        ref={colorInputRef}
        type="color"
        onChange={handleCustomColorChange}
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
};

export default ColorSelector;
